import { useEffect } from "react";
import PulseBg from "@assets/pulse/pulse-background.jpg";
import PulseBgMobile from "@assets/pulse/pulse-background-mobile.jpg";
import NoAdSettings from "@components/Advertisement/NoAdSettings";
import GAData from "@components/GAData/GAData";
import MetaTags from "@components/MetaTags/MetaTags";
import { useNavigateCarousel } from "@features/pulse/hooks/useNavigateCarousel";
import { useSetPulseArticle } from "@features/pulse/hooks/useSetPulseArticle";
import {
  getPulseSeoData,
  getPulseSliceArticles,
  PulseLayoutFactory,
} from "@features/pulse/utils/helpers";
import type { PulsePageContext } from "@features/pulse/utils/types";
import { useWindowSize } from "@hooks/useWindowSize";
import { dayjsSingaporeTimezone } from "@util/constant";
import dayjs from "dayjs";
import { useFeatureFlagVariantKey } from "posthog-js/react";

const PulsePageContent = ({
  data: { pulseArticles, pulseIndividualArticles },
}: PulsePageContext): React.ReactElement => {
  const { isScreenLG } = useWindowSize();

  const variant = useFeatureFlagVariantKey("pulse_version_1_2")?.toString();

  const isControl = variant === "control";

  const { sortedPulseArticles } = useSetPulseArticle({
    pulseArticles,
    pulseIndividualArticles: pulseIndividualArticles || [],
    variant,
  });

  const imageUrl = isScreenLG ? PulseBg : PulseBgMobile;

  const dataList = getPulseSliceArticles(
    pulseArticles,
    sortedPulseArticles,
    isControl
  );

  const {
    offset,
    activeArticle,
    handleNavigateLeftCard,
    handleNavigateRightCard,
    handleSetActiveArticle,
  } = useNavigateCarousel({
    articleList: dataList,
    variant,
  });

  const firstArticle = pulseIndividualArticles?.[0];

  const {
    title: seoTitle,
    description,
    slug,
    imageUrl: imageUrlSeo,
  } = getPulseSeoData(firstArticle);

  const gaPubDate =
    activeArticle === sortedPulseArticles.length
      ? "(not set)"
      : dayjs(firstArticle?.published)
          .tz(dayjsSingaporeTimezone)
          .format("YYYY-MM-DD HH:mm:ss");

  const PulseLayout = PulseLayoutFactory(variant || "");

  useEffect(() => {
    // Set the background color for the body to match the Pulse theme
    window.document.body.style.backgroundColor = "#0a0a0a";
  }, []);

  return (
    <div
      className="max-w-screen relative left-1/2 mx-auto flex h-svh w-screen -translate-x-1/2 flex-col overflow-hidden bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: !isControl ? `url(${imageUrl})` : "#0a0a0a",
      }}
    >
      <NoAdSettings />

      <GAData
        event="virtual_pv"
        level2="pulse"
        contentcat={1}
        contenttype={activeArticle === sortedPulseArticles.length ? "" : "1"}
        title={seoTitle}
        articleid={firstArticle?.id || ""}
        pubdate={gaPubDate}
        author={firstArticle?.authors || "(not set)"}
        gsChannels="(not set)"
        abVariant={variant}
        pagination={activeArticle}
      />

      <MetaTags
        title={seoTitle}
        description={description}
        ogDescription={description}
        ogType="website"
        slug={slug}
        imageUrl={imageUrlSeo}
        imageAlt="Pulse"
      />

      <PulseLayout
        sortedPulseArticles={dataList}
        activeArticle={activeArticle}
        handleNavigateRightCard={handleNavigateRightCard}
        handleNavigateLeftCard={handleNavigateLeftCard}
        handleSetActiveArticle={handleSetActiveArticle}
        offset={offset}
      />
    </div>
  );
};

export default PulsePageContent;
